/**
 * WebCrypto helpers for one-time secret links (Secrets Vault).
 *
 * A fresh AES-256-GCM key is generated per link and encrypts the vault item
 * client-side; only { ciphertext, iv, alg } ever reach the server. The raw
 * key travels exclusively in the URL `#k=` fragment (base64url), which
 * browsers never send to the server — end-to-end encryption by construction.
 */

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

function bytesToBase64(bytes) {
  let binary = '';
  const view = new Uint8Array(bytes);
  for (let i = 0; i < view.length; i += 1) {
    binary += String.fromCharCode(view[i]);
  }
  return btoa(binary);
}

function base64ToBytes(b64) {
  return Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
}

function bytesToBase64Url(bytes) {
  return bytesToBase64(bytes).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function base64UrlToBytes(b64url) {
  const padded = b64url.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(b64url.length / 4) * 4, '=');
  return base64ToBytes(padded);
}

/**
 * Encrypt a decrypted vault item ({ ...envelope, data }) for a secret link.
 *
 * @returns {Promise<{ ciphertext: string, iv: string, alg: 'A256GCM', keyFragment: string }>}
 *   ciphertext/iv are base64 (sent to the server); keyFragment is the
 *   base64url raw AES key destined ONLY for the URL fragment.
 */
export async function generateSecretLinkPayload(item) {
  const key = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, [
    'encrypt',
    'decrypt',
  ]);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const plaintext = textEncoder.encode(
    JSON.stringify({ type: item.type, name: item.name, data: item.data })
  );
  const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, plaintext);
  const rawKey = await crypto.subtle.exportKey('raw', key);

  return {
    ciphertext: bytesToBase64(new Uint8Array(ciphertext)),
    iv: bytesToBase64(iv),
    alg: 'A256GCM',
    keyFragment: bytesToBase64Url(new Uint8Array(rawKey)),
  };
}

/**
 * Reverse of generateSecretLinkPayload — decrypts a claimed payload with the
 * `#k=` fragment. Mirrors the reveal page's logic; kept in-app for tests and
 * decrypt parity.
 *
 * @returns {Promise<{ type: string, name: string, data: object }>}
 */
export async function decryptSecretLinkPayload({ ciphertext, iv }, keyFragment) {
  const key = await crypto.subtle.importKey(
    'raw',
    base64UrlToBytes(keyFragment),
    { name: 'AES-GCM' },
    false,
    ['decrypt']
  );
  const plaintext = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: base64ToBytes(iv) },
    key,
    base64ToBytes(ciphertext)
  );
  return JSON.parse(textDecoder.decode(plaintext));
}
